import { useSyncExternalStore } from 'react';

// Persistent portfolio filters store (Super Admin AI Portfolio page).
// Uses sessionStorage so filters survive navigation but not browser close.

export interface PortfolioFiltersState {
  searchTerm: string;
  selectedCohort: string;
  selectedArea: string;
  selectedStatus: string;
  selectedClassification: string;
  showSubmittedOnly: boolean;
  selectedDeliverable: 'all' | 'with' | 'without';
  selectedEvaluated: 'all' | 'evaluated' | 'not_evaluated';
  sortBy: 'score' | 'roi' | 'cohort' | 'fecha';
  viewMode: 'table' | 'cards';
  currentPage: number;
}

const DEFAULTS: PortfolioFiltersState = {
  searchTerm: '',
  selectedCohort: 'Todas',
  selectedArea: 'Todas',
  selectedStatus: 'Todos',
  selectedClassification: 'Todas',
  showSubmittedOnly: false,
  selectedDeliverable: 'all',
  selectedEvaluated: 'all',
  sortBy: 'score',
  viewMode: 'table',
  currentPage: 1,
};

const KEY = 'lider:admin:portfolio:filters';

function read(): PortfolioFiltersState {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

let current = read();
const listeners = new Set<() => void>();

function persist() {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* ignore */
  }
}

function setState(partial: Partial<PortfolioFiltersState>) {
  current = { ...current, ...partial };
  persist();
  listeners.forEach((l) => l());
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

const getSnapshot = () => current;
const getServerSnapshot = () => DEFAULTS;

export function usePortfolioFilters() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return [state, setState] as const;
}
